import { Component, OnInit, ChangeDetectorRef, ViewChildren, QueryList, ElementRef, AfterViewInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MultimediaService, MultimediaItem } from '@services/multimedia.service';
import { LoginComponent } from '@auth/pages/login.component';
import { AnalyticsService } from '@services/analytics.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-learn-section',
  templateUrl: './learn-section.component.html',
  styleUrls: ['./learn-section.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class LearnSectionComponent implements OnInit, AfterViewInit, OnDestroy {
  publicVideos: MultimediaItem[] = [];
  loadingPublicVideos = false;
  publicVideosPlaying = new Set<string>();
  @ViewChildren('publicVideoEl') publicVideoElems!: QueryList<ElementRef<HTMLVideoElement>>;
  private videoFramesLoaded = new Set<string>();
  private videoPosterUrls = new Map<string, string>();
  private publicVideoObserver?: IntersectionObserver;
  private userInteracted = false;

  // Modal de video fullscreen
  showFullscreenVideo = false;
  currentVideoIndex = 0;
  currentVideo: MultimediaItem | null = null;
  showRegisterModal = false;
  @ViewChild('fullscreenVideoEl') fullscreenVideoEl?: ElementRef<HTMLVideoElement>;
  isMuted = true;
  showControls = true;
  private controlsTimeout?: any;
  videoProgress = 0;
  isVideoPaused = false;
  showQuoteModal = false;

  private readonly isBrowser = typeof window !== 'undefined';

  private bottomSheet = inject(MatBottomSheet);
  private analytics = inject(AnalyticsService);

  constructor(
    private router: Router,
    private multimediaService: MultimediaService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.isBrowser) return;
    
    this.setupUserInteractionListener();
    this.loadPublicVideos();
  }

  loadPublicVideos() {
    this.loadingPublicVideos = true;
    
    // Usar operador de cache y timeout para mejorar percepción de carga
    this.multimediaService.getPublicMultimedia()
      .pipe(
        finalize(() => {
          this.loadingPublicVideos = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (videos) => {
          // Limitar a 4 videos y ordenar por fecha más reciente
          this.publicVideos = (videos || [])
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .slice(0, 4);
          
          this.cdr.detectChanges();
          
          // Configurar observer inmediatamente pero sin forzar carga de posters
          requestAnimationFrame(() => {
            this.setupPublicVideoObserver();
          });
        },
        error: (err) => {
          console.warn('Error loading public videos:', err);
          this.publicVideos = [];
          this.cdr.detectChanges();
        }
      });
  }

  private onUserInteraction = () => {
    if (!this.userInteracted) {
      this.userInteracted = true;
      setTimeout(() => {
        this.forceLoadPublicVideoPosters();
      }, 100);
    }
  };

  setupUserInteractionListener() {
    if (!this.isBrowser) return;
    document.addEventListener('click', this.onUserInteraction, { once: true, passive: true });
    document.addEventListener('touchstart', this.onUserInteraction, { once: true, passive: true });
  }

  setupPublicVideoObserver() {
    if (!this.publicVideoElems || !this.isBrowser) return;
    
    if (this.publicVideoObserver) {
      this.publicVideoObserver.disconnect();
    }
    
    // Usar intersection observer más eficiente con umbral optimizado
    this.publicVideoObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const video = entry.target as HTMLVideoElement;
        const videoId = video.getAttribute('data-multimedia-id') || '';
        const index = parseInt(video.getAttribute('data-video-index') || '0', 10);
        
        if (entry.isIntersecting) {
          // Solo cargar metadata si no se ha cargado antes
          if (!this.videoFramesLoaded.has(videoId)) {
            // Usar requestAnimationFrame para mejorar performance
            requestAnimationFrame(() => {
              this.loadVideoMetadataLazy(index, videoId);
            });
          }
        } else {
          // Pausar video si sale de vista para ahorrar recursos
          const videoRef = this.publicVideoElems?.toArray()[index];
          if (videoRef?.nativeElement && !videoRef.nativeElement.paused) {
            videoRef.nativeElement.pause();
            this.publicVideosPlaying.delete(videoId);
          }
        }
      }
    }, { 
      threshold: 0.2, // Aumentar umbral para cargar antes
      rootMargin: '50px' // Precargar cuando esté cerca del viewport
    });
    
    this.publicVideoElems.forEach(ref => {
      const el = ref.nativeElement;
      if (el) {
        this.publicVideoObserver!.observe(el);
      }
    });
  }

  forceLoadPublicVideoPosters() {
    if (!this.publicVideoElems) return;
    
    this.publicVideoElems.forEach((ref, index) => {
      const el = ref.nativeElement;
      if (el) {
        const videoId = el.getAttribute('data-multimedia-id') || '';
        if (!this.videoFramesLoaded.has(videoId)) {
          requestAnimationFrame(() => {
            this.loadVideoMetadataLazy(index, videoId);
          });
        }
      }
    });
  }

  /**
   * Carga lazy de metadata de video optimizada
   */
  private loadVideoMetadataLazy(index: number, videoId: string) {
    const videoRef = this.publicVideoElems?.toArray()[index];
    if (!videoRef?.nativeElement) return;
    
    const el = videoRef.nativeElement;
    
    // Si ya se procesó este video, no hacer nada
    if (this.videoFramesLoaded.has(videoId)) return;
    
    try {
      // Configurar video para carga rápida (mantener preload='none' en HTML)
      el.preload = 'metadata';
      
      // Manejar evento loadedmetadata de forma más eficiente
      const handleMetadata = () => {
        try {
          el.currentTime = 0.1; // Buscar frame para poster
          this.videoFramesLoaded.add(videoId);
          this.cdr.markForCheck();
        } catch (error) {
          // Si falla, marcar como procesado para evitar reintentos
          this.videoFramesLoaded.add(videoId);
        }
        el.removeEventListener('loadedmetadata', handleMetadata);
      };
      
      el.addEventListener('loadedmetadata', handleMetadata, { once: true });
      
      // Cargar metadata si no está cargada
      if (el.readyState < 1) {
        el.load();
      } else {
        handleMetadata();
      }
    } catch (error) {
      // En caso de error, marcar como procesado
      this.videoFramesLoaded.add(videoId);
    }
  }

  /**
   * Prepara el audio del video para reproducción fullscreen
   * Solo se ejecuta cuando se va a reproducir, no afecta el lazy loading
   */
  private prepareVideoAudio(videoElement: HTMLVideoElement): void {
    try {
      // Configurar audio solo cuando se va a reproducir
      videoElement.muted = this.isMuted;
      videoElement.volume = 1.0;
      
      // Si el video no tiene datos de audio cargados, cargar más información
      if (videoElement.readyState < 2) {
        // Temporalmente cambiar preload para cargar audio
        videoElement.preload = 'auto';
      }
    } catch (error) {
      console.warn('No se pudo configurar audio del video:', error);
      // Fallback: asegurar que esté muted si hay problemas
      videoElement.muted = true;
      this.isMuted = true;
    }
  }

  ngAfterViewInit(): void {
    // Usar requestAnimationFrame en lugar de setTimeout para mejor performance
    requestAnimationFrame(() => {
      this.setupPublicVideoObserver();
    });
  }

  playPublicVideoByIndex(index: number, id: string) {
    this.analytics.trackEvent('video', 'learn_section', id);
    if (this.publicVideos.length > 0 && index < this.publicVideos.length) {
      this.currentVideoIndex = index;
      this.currentVideo = this.publicVideos[index];
      this.showFullscreenVideo = true;
      this.isMuted = false;
      this.showControls = true;
      this.isVideoPaused = false;
      this.cdr.detectChanges();
      
      setTimeout(() => {
        this.initFullscreenVideo();
      }, 100);
    }
  }

  initFullscreenVideo() {
    if (this.fullscreenVideoEl?.nativeElement && this.currentVideo) {
      const video = this.fullscreenVideoEl.nativeElement;
      video.src = this.currentVideo.url;
      
      // Preparar configuración de audio antes de cargar
      this.prepareVideoAudio(video);
      
      video.load();
      
      // Intentar reproducir con audio, fallback a muted si falla
      video.play().then(() => {
        // Reproducción exitosa
        this.cdr.detectChanges();
      }).catch(() => {
        // Fallback: reproducir muted si falla por políticas del navegador
        video.muted = true;
        this.isMuted = true;
        video.play().catch(() => console.warn('No se pudo reproducir el video'));
        this.cdr.detectChanges();
      });
      
      // Event listeners existentes
      video.addEventListener('timeupdate', () => {
        if (video.duration) {
          this.videoProgress = (video.currentTime / video.duration) * 100;
          this.cdr.detectChanges();
        }
      });

      video.addEventListener('play', () => {
        this.isVideoPaused = false;
        this.cdr.detectChanges();
      });

      video.addEventListener('pause', () => {
        this.isVideoPaused = true;
        this.showControls = true;
        this.cdr.detectChanges();
      });

      video.addEventListener('ended', () => {
        const isLastVideo = this.currentVideoIndex === this.publicVideos.length - 1;
        if (isLastVideo) {
          this.showRegisterModal = true;
          this.cdr.detectChanges();
        } else {
          this.nextVideo();
        }
      });
    }
  }

  nextVideo() {
    if (this.publicVideos.length === 0) return;
    
    const isLastVideo = this.currentVideoIndex === this.publicVideos.length - 1;
    
    if (isLastVideo) {
      this.showRegisterModal = true;
      this.cdr.detectChanges();
      return;
    }
    
    this.currentVideoIndex = this.currentVideoIndex + 1;
    this.currentVideo = this.publicVideos[this.currentVideoIndex];
    this.videoProgress = 0;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.initFullscreenVideo();
    }, 100);
  }

  closeFullscreenVideo() {
    if (this.fullscreenVideoEl?.nativeElement) {
      this.fullscreenVideoEl.nativeElement.pause();
      this.fullscreenVideoEl.nativeElement.src = '';
    }
    this.showFullscreenVideo = false;
    this.currentVideo = null;
    this.currentVideoIndex = 0;
    this.showRegisterModal = false;
    this.isVideoPaused = false;
    this.cdr.detectChanges();
  }

  toggleMute() {
    if (this.fullscreenVideoEl?.nativeElement) {
      const video = this.fullscreenVideoEl.nativeElement;
      this.isMuted = !this.isMuted;
      video.muted = this.isMuted;
      
      // Asegurar volumen correcto cuando se desmutea
      if (!this.isMuted) {
        video.volume = 1.0;
      }
      
      this.cdr.detectChanges();
    }
  }

  toggleVideoPlayPause() {
    if (this.fullscreenVideoEl?.nativeElement) {
      if (this.fullscreenVideoEl.nativeElement.paused) {
        this.fullscreenVideoEl.nativeElement.play();
        this.isVideoPaused = false;
      } else {
        this.fullscreenVideoEl.nativeElement.pause();
        this.isVideoPaused = true;
      }
      this.showControls = true;
    }
  }

  closeRegisterModal() {
    this.showRegisterModal = false;
    this.cdr.detectChanges();
  }

  goToRegister() {
    this.closeRegisterModal();
    this.closeFullscreenVideo();
    this.router.navigate(['/auth/register']);
  }

  openRegisterPromptModal() {
     this.showQuoteModal = true;
  }

  togglePublicVideoByIndex(index: number, id: string) {
    const videoRef = this.publicVideoElems?.toArray()[index];
    if (videoRef?.nativeElement) {
      const video = videoRef.nativeElement;
      if (video.paused) {
        // Preparar audio antes de reproducir en el carrusel
        this.prepareCarouselVideoAudio(video);
        
        video.play().then(() => {
          this.publicVideosPlaying.add(id);
          this.cdr.detectChanges();
        }).catch(() => {
          // Fallback: reproducir muted si falla
          video.muted = true;
          video.play().then(() => {
            this.publicVideosPlaying.add(id);
            this.cdr.detectChanges();
          }).catch(() => {});
        });
      } else {
        video.pause();
        this.publicVideosPlaying.delete(id);
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Prepara audio específicamente para videos del carrusel
   */
  private prepareCarouselVideoAudio(videoElement: HTMLVideoElement): void {
    try {
      // En el carrusel, empezar muted para evitar interrumpir al usuario
      videoElement.muted = true;
      videoElement.volume = 0.7; // Volumen más bajo para el carrusel
      
      // Si el usuario interactúa, puede escuchar audio
      if (this.userInteracted) {
        // Permitir audio después de interacción del usuario
        setTimeout(() => {
          if (!videoElement.paused) {
            videoElement.muted = false;
          }
        }, 500);
      }
    } catch (error) {
      // Mantener muted en caso de error
      videoElement.muted = true;
    }
  }

  isPublicVideoPlaying(id: string): boolean {
    return this.publicVideosPlaying.has(id);
  }

  onPublicVideoMetaByIndex(index: number) {
    const videoRef = this.publicVideoElems?.toArray()[index];
    if (videoRef?.nativeElement) {
      const el = videoRef.nativeElement;
      const videoId = el.getAttribute('data-multimedia-id') || '';
      
      if (this.videoPosterUrls.has(videoId)) {
        return;
      }

      try {
        if (el.readyState === 0) {
          el.load();
        }

        el.pause();
        el.currentTime = 0.1;
        
        setTimeout(() => {
          this.videoFramesLoaded.add(videoId);
          this.cdr.detectChanges();
        }, 100);
      } catch (e) {
        // Ignorar errores
      }
    }
  }

  getVideoPosterUrl(videoId: string): string | null {
    return this.videoPosterUrls.get(videoId) || null;
  }

  onPublicVideoSeeked(index: number) {
    const videoRef = this.publicVideoElems?.toArray()[index];
    if (videoRef?.nativeElement) {
      const el = videoRef.nativeElement;
      const videoId = el.getAttribute('data-multimedia-id') || '';
      
      if (!el.paused && !this.publicVideosPlaying.has(videoId)) {
        el.pause();
      }
      
      this.videoFramesLoaded.add(videoId);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.publicVideoObserver) {
      this.publicVideoObserver.disconnect();
    }
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }
    document.removeEventListener('click', this.onUserInteraction);
    document.removeEventListener('touchstart', this.onUserInteraction);
  }

    closeQuoteModal(): void {
    this.showQuoteModal = false;
  }

    goToRegisterUser(): void {
    this.closeQuoteModal();
    this.router.navigate(['/auth/register']);
  }

  goToLogin(): void {
    this.closeQuoteModal();
    this.bottomSheet.open(LoginComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'login-bottom-sheet',
    });
  }
}
